// src/components/admin/tabs/UserStatsTab.jsx
// 用户统计：账号管理 + 服务端聚合的个人用量、费用、积分与小故事板抽卡统计。
// 视觉沿用管理工作台语言，个人详情拆为独立组件，避免继续放大本文件。
// props 契约：({ projectId, projectOptions, onProjectSelect, selectedProjectId, onProjectsChanged })
// 本 tab 只用到 projectId / projectOptions，其余由父级传入时忽略（与原实现一致）。
import { appConfirm, appPrompt } from '../../../services/appDialogs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserPlus, KeyRound, LogOut, Ban, Unlock } from "lucide-react";
import {
  fetchUserStats,
  fetchAdminUsers,
  createAdminUser,
  resetAdminUserPassword,
  updateAdminUserStatus,
  updateAdminUserRole,
  revokeAdminUserSessions,
} from "../../../services/adminService";
import { displayUserId, useAdminScope, userMatchesFilter } from "../adminUtils";
import {
  Panel,
  EmptyText,
  ErrorNotice,
  FilterInput,
  FilterSelect,
  PrimaryButton,
  IconAction,
  StatusBadge,
  AdminScopeFilterBar,
  ADMIN_ACCENT,
} from "../adminUi.jsx";
import UserStatsRangeControl from './UserStatsRangeControl.jsx';
import {
  buildOverviewTimestamps,
  createDefaultCustomRange,
} from './userStatsRange.js';
import UserUsageDetailDialog from './UserUsageDetailDialog.jsx';
import AppSelect from '../../ui/AppSelect.jsx';
import { USER_ROLE_LABELS } from '../../../utils/userRoles';

const USER_ROLE_OPTIONS = Object.entries(USER_ROLE_LABELS);

const UserRoleSelect = ({ value, onChange, label }) => (
  <AppSelect
    aria-label={label}
    value={value || 'member'}
    onChange={(event) => onChange(event.target.value)}
    className="h-8 px-2.5 rounded-md text-xs outline-none"
    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
  >
    {USER_ROLE_OPTIONS.map(([role, text]) => <option key={role} value={role}>{text}</option>)}
  </AppSelect>
);

const COLUMN_LABELS = {
  userId: '用户', cost: '历史费用', pointsSpent: '消耗积分', drawCount: '抽卡次数',
  averageDrawsPerSegment: '平均/片段', drawnSegmentCount: '已抽片段', generationCount: '生成用量',
  projectCount: '项目', activeSessionCount: '在线', role: '角色', status: '状态', lastActiveAt: '最近活跃',
};
const SORTABLE_COLUMNS = [
  'userId', 'cost', 'pointsSpent', 'drawCount', 'averageDrawsPerSegment', 'drawnSegmentCount',
  'generationCount', 'projectCount', 'activeSessionCount', 'role', 'status', 'lastActiveAt',
];

const UserStatsTab = ({ projectId, projectOptions }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [range, setRange] = useState('all');
  const [customRange, setCustomRange] = useState(createDefaultCustomRange);
  const [filters, setFilters] = useState({ keyword: '', status: '', limit: 500 });
  const [scope, setScope] = useAdminScope(projectId);
  const [newUser, setNewUser] = useState({ name: '', password: '', role: 'member' });
  const [actionMessage, setActionMessage] = useState('');
  const [detailRow, setDetailRow] = useState(null);
  const [sortKey, setSortKey] = useState('cost');
  const listRequestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setError('');
    setActionMessage('');
    try {
      const [start, end] = buildOverviewTimestamps(range, customRange);
      const [statsRows, adminRows] = await Promise.all([
        fetchUserStats(start, end, scope.projectId, scope.userId),
        fetchAdminUsers(filters),
      ]);
      const statsByUser = new Map((statsRows || []).map(row => [row.userId, row]));
      if (requestId !== listRequestRef.current) return;
      const merged = (adminRows || []).map(user => {
        const stat = statsByUser.get(user.userId) || {};
        return {
          ...stat,
          ...user,
          projectCount: stat.projectCount ?? user.projectCount ?? 0,
          generationCount: stat.generationCount ?? 0,
          imageCount: stat.imageCount ?? 0,
          videoCount: stat.videoCount ?? 0,
          videoSeconds: stat.videoSeconds ?? 0,
          cost: stat.cost ?? 0,
          pointsSpent: stat.pointsSpent ?? 0,
          drawCount: stat.drawCount ?? 0,
          drawnSegmentCount: stat.drawnSegmentCount ?? 0,
          averageDrawsPerSegment: stat.averageDrawsPerSegment ?? 0,
          lastActiveAt: stat.lastActiveAt ?? user.lastLoginAt,
        };
      });
      setRows(merged.filter(row => userMatchesFilter(row.userId, scope.userId)));
      setLoadedOnce(true);
    } catch (e) {
      if (requestId !== listRequestRef.current) return;
      console.error(e);
      setError(e?.message || '用户统计加载失败');
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [customRange, filters, range, scope.projectId, scope.userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { listRequestRef.current += 1; }, []);

  const runAction = async (action, successText) => {
    setActionMessage('');
    try {
      await action();
      setActionMessage(successText);
      await load();
      return true;
    } catch (e) {
      console.error(e);
      setError(e?.message || '操作失败');
      return false;
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim()) {
      setError('用户名不能为空');
      return;
    }
    const ok = await runAction(
      () => createAdminUser({
        name: newUser.name.trim(),
        password: newUser.password,
        role: newUser.role,
        status: 'active',
      }),
      '用户已创建'
    );
    if (ok) setNewUser({ name: '', password: '', role: 'member' });
  };

  const handleResetPassword = async (row) => {
    const password = await appPrompt('输入新密码。留空确认后表示清空密码，让用户下次首次进入时自行设置。', '', { title: '重置密码', inputType: 'password' });
    if (password == null) return;
    const clear = password.trim() === '';
    runAction(
      () => resetAdminUserPassword(row.id, { password, clear }),
      clear ? '密码已清空，会话已踢下线' : '密码已重置，会话已踢下线'
    );
  };

  const handleStatus = async (row, status) => {
    const text = status === 'disabled' ? '禁用' : '启用';
    if (!(await appConfirm(`确认${text}用户「${displayUserId(row.userId)}」？`))) return;
    runAction(() => updateAdminUserStatus(row.id, status), `用户已${text}`);
  };

  const handleRole = async (row, role) => {
    if (role === (row.role || 'member')) return;
    const text = `改为${USER_ROLE_LABELS[role] || role}`;
    if (!(await appConfirm(`确认将「${displayUserId(row.userId)}」${text}？`))) return;
    runAction(() => updateAdminUserRole(row.id, role), '角色已更新');
  };

  const handleRevokeSessions = async (row) => {
    if (!(await appConfirm(`确认踢下线用户「${displayUserId(row.userId)}」的所有设备？`))) return;
    runAction(() => revokeAdminUserSessions(row.id), '用户会话已全部失效');
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    if (typeof av === 'string') return bv.localeCompare(av);
    return bv - av;
  }), [rows, sortKey]);

  return (
    <div>
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={load}
        loading={loading}
      >
        <UserStatsRangeControl
          rangeKey={range}
          onRangeChange={setRange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
        <FilterInput label="用户关键词" value={filters.keyword} onChange={(v) => setFilters(f => ({ ...f, keyword: v }))} />
        <FilterSelect label="状态" value={filters.status} options={['', 'active', 'disabled']} onChange={(v) => setFilters(f => ({ ...f, status: v }))} />
        <FilterInput label="条数" type="number" value={filters.limit} onChange={(v) => setFilters(f => ({ ...f, limit: v }))} />
      </AdminScopeFilterBar>
      {error && <ErrorNotice message={error} />}
      {actionMessage && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm mb-4 animate-in fade-in slide-in-from-top-1 duration-200"
          style={{ backgroundColor: 'rgba(34,197,94,0.12)', border: '1px solid var(--color-success)', color: 'var(--color-success)' }}>
          {actionMessage}
        </div>
      )}

      <div className="mb-4">
        <Panel title="创建用户" sub="新用户使用初始密码首次登录">
          <div className="flex flex-wrap gap-3 items-end">
            <FilterInput label="新用户" value={newUser.name} onChange={(v) => setNewUser(u => ({ ...u, name: v }))} />
            <FilterInput label="初始密码" type="password" value={newUser.password} onChange={(v) => setNewUser(u => ({ ...u, password: v }))} />
            <div>
              <span className="text-label block mb-1.5">角色</span>
              <UserRoleSelect label="新用户角色" value={newUser.role} onChange={(v) => setNewUser(u => ({ ...u, role: v }))} />
            </div>
            <PrimaryButton onClick={handleCreateUser} className="inline-flex items-center gap-1.5">
              <UserPlus size={14} /> 创建用户
            </PrimaryButton>
          </div>
        </Panel>
      </div>

      <Panel
        title="用户列表"
        sub="费用、积分和抽卡均按所选范围统计；点击行查看个人完整明细"
        right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{sorted.length} 个用户</span>}
      >
        {!loadedOnce && loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton h-8" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label" style={{ textAlign: "left" }}>
                  {SORTABLE_COLUMNS.map(k => (
                    <th key={k} onClick={() => setSortKey(k)} className="group font-medium pb-2.5 pr-4 cursor-pointer hover:underline whitespace-nowrap">
                      {COLUMN_LABELS[k]}
                      <span className={`transition-opacity duration-150 ${sortKey === k ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}> ↓</span>
                    </th>
                  ))}
                  <th className="font-medium pb-2.5 pr-4">操作</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td colSpan={SORTABLE_COLUMNS.length + 1}><EmptyText text="暂无数据" /></td></tr>
                ) : sorted.map((r, i) => (
                  <tr key={r.id ?? r.userId ?? i} onClick={() => setDetailRow(r)}
                    style={{ borderTop: '1px solid var(--color-border-secondary)', cursor: 'pointer' }}
                    className="hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150">
                    <td className="py-2 pr-4 text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>{displayUserId(r.userId)}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs font-semibold" style={{ color: ADMIN_ACCENT }}>¥{Number(r.cost ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{Number(r.pointsSpent ?? 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs font-semibold" style={{ color: 'var(--color-purple-primary)' }}>{Number(r.drawCount ?? 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{Number(r.averageDrawsPerSegment ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{Number(r.drawnSegmentCount ?? 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{Number(r.generationCount ?? 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{r.projectCount ?? 0}</td>
                    <td className="py-2 pr-4 font-mono-num text-xs">{r.activeSessionCount ?? 0}</td>
                    <td className="py-2 pr-4">
                      {r.role === 'admin'
                        ? <StatusBadge label="管理员" color={ADMIN_ACCENT} />
                        : <StatusBadge label={USER_ROLE_LABELS[r.role || 'member'] || r.role} />}
                    </td>
                    <td className="py-2 pr-4">
                      <StatusBadge label={r.status} color={r.status === 'active' ? 'var(--color-success)' : 'var(--color-error)'} />
                    </td>
                    <td className="py-2 pr-4 font-mono-num text-2xs whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>
                      {r.lastActiveAt ? new Date(r.lastActiveAt).toLocaleString() : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                        <IconAction title="重置密码" onClick={() => handleResetPassword(r)} icon={KeyRound} />
                        <IconAction title="踢下线" onClick={() => handleRevokeSessions(r)} icon={LogOut} />
                        {r.status === 'active'
                          ? <IconAction title="禁用" onClick={() => handleStatus(r, 'disabled')} icon={Ban} danger />
                          : <IconAction title="启用" onClick={() => handleStatus(r, 'active')} icon={Unlock} />}
                        <UserRoleSelect label={`修改${displayUserId(r.userId)}的角色`} value={r.role} onChange={(role) => handleRole(r, role)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {detailRow ? (
        <UserUsageDetailDialog
          key={detailRow.id ?? detailRow.userId}
          user={detailRow}
          initialRangeKey={range}
          initialCustomRange={customRange}
          onClose={() => setDetailRow(null)}
        />
      ) : null}
    </div>
  );
};

export default UserStatsTab;
